import { useState } from 'react';
import { Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TAG_COLORS } from '@/lib/colors';
import { cn } from '@/lib/utils';

export interface ColorSwatchPickerProps {
  value: string;
  onChange: (hex: string) => void;
  /** Visual size of the trigger swatch. Default `sm`. */
  size?: 'sm' | 'md';
}

/**
 * Color picker rebuilt as a Popover-triggered grid (slice 9b polish).
 * The previous inline 9-swatch row competed with the name input for
 * horizontal space on every status / tag edit form; collapsing it
 * behind a single colored dot button reads cleaner and keeps the
 * row visually quiet by default.
 *
 * The selected color shows in the trigger; opening the popover
 * reveals the full palette with a checkmark on the active swatch.
 */
export const ColorSwatchPicker = ({
  value,
  onChange,
  size = 'sm',
}: ColorSwatchPickerProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          'ring-offset-background focus-visible:ring-ring inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full ring-offset-2 transition-all hover:scale-110 focus-visible:ring-2 focus-visible:outline-none',
          size === 'sm' ? 'h-6 w-6' : 'h-8 w-8',
        )}
        style={{ background: value }}
        aria-label="Pick a color"
      />
      <PopoverContent align="start" sideOffset={6} className="w-auto p-2">
        <div
          className="grid grid-cols-9 gap-1.5"
          role="listbox"
          aria-label="Color palette"
        >
          {TAG_COLORS.map((c) => {
            const active = value === c.hex;
            return (
              <button
                key={c.hex}
                type="button"
                role="option"
                aria-selected={active}
                aria-label={c.name}
                onClick={() => {
                  onChange(c.hex);
                  setOpen(false);
                }}
                className={cn(
                  'ring-offset-background relative h-6 w-6 cursor-pointer rounded-full ring-offset-2 transition-all',
                  active ? 'ring-ring ring-2' : 'hover:scale-110',
                )}
                style={{ background: c.hex }}
              >
                {active ? (
                  <Check
                    className="absolute inset-0 m-auto size-3.5 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]"
                    aria-hidden="true"
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
